'use client';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useState, useEffect, useRef, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import Section from '@repo/ui/src/components/Section';
import SearchAutocomplete from '@components/shared/SearchAutocomplete';
import { searchSubmitted } from '@repo/ui/src/services/analytics';
import { MetaProps } from '@repo/ui/src/types/meta';
import Seo, { Props as SeoProps } from '@repo/ui/src/components/Seo';
import SearchResultsList from './_SearchResultsList';

interface TabProps {
  tabName: string;
  searchType: string;
  redirectUrl?: string;
}

interface Props {
  _meta: MetaProps;
  title: string;
  searchPlaceholder: string;
  body?: any;
  tabLabel: string;
  pageTabs: {
    tabs: Array<TabProps>;
    _meta: MetaProps;
  };
  resultTextSingular: string;
  resultTextPlural: string;
  slotContent: Array<SeoProps>;
}

const SearchResultsBody = ({
  title,
  searchPlaceholder,
  tabLabel,
  pageTabs,
  resultTextSingular,
  resultTextPlural,
  slotContent,
}: Props) => {
  const tabs = pageTabs ? pageTabs.tabs : [];
  const { query } = useCurrentUrl();
  const tabsActions = useRef<any>();

  const searchTerm = query?.get('term') ?? '';
  const page = query.get('page') ?? '1';
  const searchType = query?.get('type') as string;
  const getCurrentTab = () => {
    const tabIndex = tabs?.findIndex((x) =>
      searchType ? x.searchType === searchType : x.tabName.includes(searchTerm),
    );
    return tabIndex >= 0 ? tabIndex : 0;
  };
  const [currentTab, setCurrentTab] = useState(getCurrentTab);

  const seo = slotContent?.[0];

  const onSearch = useCallback((term: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('term', term);
    window.history.pushState(null, '', url);
  }, []);

  useEffect(() => {
    if (tabsActions.current) {
      tabsActions.current.updateIndicator();
    }
  }, [currentTab]);

  useEffect(() => {
    searchSubmitted({ query: searchTerm });
  }, [searchTerm]);
  useEffect(() => {
    setCurrentTab(getCurrentTab);
  }, [searchType]);

  return (
    <>
      {seo && <Seo {...seo} />}
      <Section gutterX gutterY className="tw-flex-1">
        <Box mt={{ xs: 2, md: 5 }}>
          <Typography component="h1" variant="body1" align="center" sx={{ textTransform: 'none' }}>
            {title || 'Search products, recipes, or support information'}
          </Typography>
        </Box>

        <div id="instructions" className="sr-only">
          Begin typing to search, use arrow keys to navigate, Enter to select
        </div>

        <SearchAutocomplete
          searchTerm={searchTerm}
          placeholder={searchPlaceholder}
          className="tw-max-w-[35rem] tw-my-4 tw-mx-auto"
          onSearch={onSearch}
          aria-activedescendant={currentTab}
        />

        {tabs?.length > 0 && searchTerm && (
          <>
            <Box display="flex" justifyContent="center" sx={{ margin: `0.5rem -1.25rem` }}>
              <Tabs
                variant="scrollable"
                scrollButtons
                value={currentTab}
                onChange={(e, newValue) => {
                  // @ts-expect-error - this is going to change soon anyway
                  if (!e.target.href) {
                    setCurrentTab(newValue);
                  }
                }}
                action={tabsActions}
                aria-label={`${tabLabel || 'Select a search type'} ${tabs[currentTab]?.tabName}`}
                allowScrollButtonsMobile
              >
                {tabs.map(({ tabName, redirectUrl }) =>
                  redirectUrl ? (
                    <Tab
                      key={tabName}
                      href={`${redirectUrl}${searchTerm}`}
                      component="a"
                      target="_blank"
                      label={tabName}
                    />
                  ) : (
                    <Tab key={tabName} label={tabName} id={tabName} />
                  ),
                )}
              </Tabs>
            </Box>
            <SearchResultsList
              {...tabs[currentTab]}
              page={page}
              searchTerm={searchTerm}
              searchType={tabs[currentTab]?.searchType}
              resultTextSingular={resultTextSingular}
              resultTextPlural={resultTextPlural}
            />
          </>
        )}
      </Section>
    </>
  );
};

export default SearchResultsBody;
